import { useDispatch } from 'react-redux';
import _ from 'lodash';
import { logErrorToConsole, SearchStatus, TravelTypes } from '../constants';
import {
  saveBrochureRequest,
  updateBrochureField,
  setIsViewingBrochure,
} from '../store/slices/brochure';
import arePassengerCountsEqual from '../helpers/checkArePassengerCountsEqual';

const useBrochureOptionRemoval = () => {
  const dispatch = useDispatch();

  const getNestedProperty = (obj, path) =>
    path.split('.').reduce((acc, part) => acc && acc[part], obj);

  const handleSegmentRemoval = ({
    brochureId,
    currentBrochure,
    travelType,
    segmentKeyValue,
    segmentKey,
    findTripSegmentfn,
    isSavedToDatabase,
    product,
    findHotelRoomfn,
    findRateCategoryfn,
    findRateOptionfn,
    cabinCategory,
    rateOption,
    selectedRoomIndex,
    itinerary,
    staterooms,
  }) => {
    if (!currentBrochure || !currentBrochure.trips) {
      console.error('currentBrochure or currentBrochure.trips is undefined or null');
      return;
    }

    // Find the trip index based on brochureId and the specified travelType
    const tripIndex = currentBrochure.trips.findIndex((trip) => {
      if (trip.id !== brochureId || !trip.tripSegments) return false;

      return trip.tripSegments.some((segment) => {
        if (segment.itinerary.mode !== travelType) return false;

        switch (travelType) {
          case TravelTypes.flight:
            return (
              segment.itinerary.flightKey === segmentKeyValue &&
              segment.itinerary.startDateTime === itinerary?.startDateTime &&
              segment.itinerary.endDateTime === itinerary?.endDateTime &&
              arePassengerCountsEqual(trip?.staterooms ?? trip?.roomSearchInput, staterooms)
            );
          case TravelTypes.carrental:
            return (
              segment.itinerary.uniqueKey === segmentKeyValue &&
              segment.itinerary.startDateTime === itinerary?.startDateTime &&
              segment.itinerary.endDateTime === itinerary?.endDateTime &&
              arePassengerCountsEqual(trip?.staterooms ?? trip?.roomSearchInput, staterooms)
            );
          case TravelTypes.tour:
            return segment.itinerary.uniqueKey === segmentKeyValue;
          case TravelTypes.hotel:
            return (
              segment.itinerary.hotelKey === segmentKeyValue &&
              segment.itinerary.startDateTime === itinerary?.startDateTime &&
              segment.itinerary.endDateTime === itinerary?.endDateTime &&
              arePassengerCountsEqual(trip?.staterooms ?? trip?.roomSearchInput, staterooms)
            );
          case TravelTypes.cruise:
            return (
              segment.itinerary?.itineraryDetail?.itineraryId === segmentKeyValue &&
              segment?.itinerary?.itineraryDetail?.itineraryDepartDate ===
                itinerary?.itineraryDepartDate &&
              segment?.itinerary?.itineraryDetail?.itineraryReturnDate ===
                itinerary?.itineraryReturnDate &&
              arePassengerCountsEqual(trip?.staterooms ?? trip?.roomSearchInput, staterooms)
            );
          default:
            return false;
        }
      });
    });

    if (tripIndex === -1) {
      logErrorToConsole(
        `Trip not found in currentBrochure or no ${travelType} segments in the trip`,
      );
      return;
    }

    // Get the current trip using the tripIndex
    const currentTrip = currentBrochure.trips[tripIndex];

    // Clone the trip to avoid mutating the state directly
    const updatedTrip = JSON.parse(JSON.stringify(currentTrip));

    // Find the segment in the updated trip using the provided function
    let segmentToRemove;
    if (travelType === TravelTypes.cruise) {
      segmentToRemove = findTripSegmentfn(updatedTrip);
    } else {
      segmentToRemove = findTripSegmentfn(updatedTrip.tripSegments, segmentKeyValue);
    }

    if (!segmentToRemove) {
      console.error(`${travelType} segment not found in trip`);
      return;
    }

    const key =
      travelType === TravelTypes.cruise
        ? 'itinerary.itineraryDetail.itineraryId'
        : `itinerary.${segmentKey}`;

    if (travelType === TravelTypes.hotel) {
      // Find the matching hotel product
      const matchedProduct = findHotelRoomfn(
        segmentToRemove.itinerary.hotel.products,
        product.code,
      );
      if (matchedProduct) {
        // Remove the matched product from the hotel's products array
        _.remove(segmentToRemove.itinerary.hotel.products, (p) => p.code === product.code);

        // If no products are left, remove the entire hotel trip segment
        if (segmentToRemove.itinerary.hotel.products.length === 0) {
          updatedTrip.tripSegments = updatedTrip.tripSegments.filter(
            (ts) => getNestedProperty(ts, key) !== getNestedProperty(segmentToRemove, key),
          );
        }
      }
    } else if (travelType === TravelTypes.cruise) {
      // Find the selected cabin category for the cruise
      const selectedCategory = findRateCategoryfn(
        segmentToRemove.itinerary.cabinCategories[selectedRoomIndex].cabinCategories,
        cabinCategory,
      );
      if (selectedCategory) {
        // Find the selected rate option within the category
        const selectedRate = findRateOptionfn(selectedCategory.rateOptions, rateOption);
        if (selectedRate) {
          // Remove the selected rate option
          _.remove(selectedCategory.rateOptions, (r) => r === selectedRate);
          // If no rate options are left, remove selectedCategory
          if (selectedCategory.rateOptions.length === 0) {
            _.remove(
              segmentToRemove.itinerary.cabinCategories[selectedRoomIndex].cabinCategories,
              (r) => r === selectedCategory,
            );
          }
        }
      }
      if (
        segmentToRemove.itinerary.cabinCategories[selectedRoomIndex].cabinCategories.length === 0
      ) {
        // if no cabinCategories are left, remove cruise segment
        updatedTrip.tripSegments = updatedTrip.tripSegments.filter(
          (ts) => getNestedProperty(ts, key) !== getNestedProperty(segmentToRemove, key),
        );
      }
    } else {
      // For other travel types, remove the segment as usual
      updatedTrip.tripSegments = updatedTrip.tripSegments.filter(
        (ts) => getNestedProperty(ts, key) !== getNestedProperty(segmentToRemove, key),
      );
    }

    // Check if the trip has no segments left after removal
    if (updatedTrip.tripSegments.length === 0) {
      // Remove the entire trip from the brochure
      const updatedBrochure = {
        ...currentBrochure,
        trips: [
          ...currentBrochure.trips.slice(0, tripIndex),
          ...currentBrochure.trips.slice(tripIndex + 1),
        ],
      };
      dispatch(
        saveBrochureRequest({
          ...updatedBrochure,
          brochureId,
          savedBrochureId: brochureId,
          doSaveToDatabase: isSavedToDatabase,
          resultsPageUrl: updatedBrochure?.trips?.[0]?.resultsPageUrl ?? null,
        }),
      );
      dispatch(
        updateBrochureField({
          fieldName: 'saveBrochureStatus',
          fieldValue: isSavedToDatabase ? SearchStatus.SEARCHING : SearchStatus.INITIAL,
        }),
      );
      if (updatedBrochure?.trips?.length === 0) dispatch(setIsViewingBrochure(false));
      return;
    }

    // If the trip still has segments, update the trip in the brochure
    const updatedBrochure = {
      ...currentBrochure,
      trips: [
        ...currentBrochure.trips.slice(0, tripIndex),
        updatedTrip,
        ...currentBrochure.trips.slice(tripIndex + 1),
      ],
    };

    // Dispatch the updated brochure to save it
    dispatch(
      saveBrochureRequest({
        ...updatedBrochure,
        brochureId,
        savedBrochureId: brochureId,
        doSaveToDatabase: isSavedToDatabase,
        resultsPageUrl: updatedBrochure?.trips?.[0]?.resultsPageUrl ?? null,
      }),
    );
    dispatch(
      updateBrochureField({
        fieldName: 'saveBrochureStatus',
        fieldValue: isSavedToDatabase ? SearchStatus.SEARCHING : SearchStatus.INITIAL,
      }),
    );
    if (updatedBrochure?.trips?.length === 0) dispatch(setIsViewingBrochure(false));
  };

  return handleSegmentRemoval;
};

export default useBrochureOptionRemoval;
